import {
  addDays,
  eachMonthOfInterval,
  endOfDay,
  endOfMonth,
  format,
  differenceInCalendarDays,
  differenceInCalendarMonths,
  parseISO,
  startOfDay,
  startOfMonth,
} from "date-fns";

export type RawPoint = {
  date: string; // ISO
  value?: number; // sessions line
  totalUsers?: number;
  registeredUsers?: number;
  guestUsers?: number;
  newRegisteredUsers?: number;
  newGuestUsers?: number;
};

export type BucketedPoint = {
  date: string;
  label: string;
  x?: number;
  y?: number;
  value?: number;
  totalUsers?: number;
  registeredUsers?: number;
  guestUsers?: number;
  newRegisteredUsers?: number;
  newGuestUsers?: number;
};

type AggMode = "sum" | "avg";

const FIELD_MODES_SESSIONS: Record<keyof RawPoint, AggMode | undefined> = {
  date: undefined,
  value: "sum",
  totalUsers: "avg",
  registeredUsers: "avg",
  guestUsers: "avg",
  newRegisteredUsers: "avg",
  newGuestUsers: "avg",
};

const FIELD_MODES_USERS: Record<keyof RawPoint, AggMode | undefined> = {
  date: undefined,
  value: "sum",
  totalUsers: "avg",
  registeredUsers: "avg",
  guestUsers: "avg",
  newRegisteredUsers: "avg",
  newGuestUsers: "avg",
};

const FIELDS: (keyof RawPoint)[] = [
  "value",
  "totalUsers",
  "registeredUsers",
  "guestUsers",
  "newRegisteredUsers",
  "newGuestUsers",
];

function sumInto(target: any, src: any, keys: (keyof RawPoint)[]) {
  for (const k of keys) {
    if (src[k] != null) target[k] = (target[k] || 0) + (src[k] as number);
  }
}

export function bucketSeries(raw: RawPoint[], maxPoints: number, view: "sessions" | "users"): BucketedPoint[] {
  if (!raw.length) return [];

  const sorted = [...raw].sort((a, b) => parseISO(a.date).getTime() - parseISO(b.date).getTime());
  const start = startOfDay(parseISO(sorted[0].date));
  const end = endOfDay(parseISO(sorted[sorted.length - 1].date));

  const days = differenceInCalendarDays(end, start) + 1;
  const months = differenceInCalendarMonths(end, start) + 1;

  const MODES = view === "sessions" ? FIELD_MODES_SESSIONS : FIELD_MODES_USERS;

  // Monthly mode
  if (months >= 10) {
    const monthsList = eachMonthOfInterval({ start: startOfMonth(start), end: endOfMonth(end) });
    return monthsList.map((m) => {
      const mStart = startOfMonth(m);
      const mEnd = endOfMonth(m);
      const inMonth = sorted.filter((p) => {
        const d = parseISO(p.date);
        return d >= mStart && d <= mEnd;
      });

      const acc: any = {};
      for (const p of inMonth) sumInto(acc, p, FIELDS);

      for (const k of FIELDS) {
        if (acc[k] == null) continue;
        const mode = MODES[k];
        if (mode === "avg" && inMonth.length > 0) {
          acc[k] = Math.round((acc[k] as number) / inMonth.length);
        }
      }

      return {
        ...acc,
        date: m.toISOString(),
        label: format(m, "MMM"),
      } as BucketedPoint;
    });
  }

  // Daily mode
  if (days <= maxPoints) {
    return sorted.map((p) => ({
      ...p,
      label: format(parseISO(p.date), "d MMM"),
    })) as BucketedPoint[];
  }

  // Bucket mode
  const bucketSize = Math.ceil(days / maxPoints);
  const buckets: BucketedPoint[] = [];

  let cursor = start;
  while (cursor <= end) {
    const bucketEnd = endOfDay(addDays(cursor, bucketSize - 1)) < end ? endOfDay(addDays(cursor, bucketSize - 1)) : end;
    const inBucket = sorted.filter((p) => {
      const d = parseISO(p.date);
      return d >= cursor && d <= bucketEnd;
    });

    if (inBucket.length > 0) {
      const acc: any = {};
      for (const p of inBucket) sumInto(acc, p, FIELDS);

      for (const k of FIELDS) {
        if (acc[k] == null) continue;
        const mode = MODES[k];
        if (mode === "avg" && inBucket.length > 0) {
          acc[k] = Math.round((acc[k] as number) / inBucket.length);
        }
      }

      // Fix duplicate date labels for single-day buckets
      const startDate = format(cursor, "d MMM");
      const endDate = format(bucketEnd, "d MMM");
      const label = startDate === endDate ? startDate : `${format(cursor, "d")}-${format(bucketEnd, "d MMM")}`;

      buckets.push({
        ...acc,
        date: cursor.toISOString(),
        label,
      });
    }

    cursor = addDays(bucketEnd, 1);
  }

  return buckets;
}